export interface SystemObject {
  id: number;
  name: string;
  hidden: boolean;
  natures: Array<NatureInstance>;
  properties: Array<Property>;
  children?: Array<SystemObject>;
  parents?: Array<SystemObject>;
  path?: Array<number>;
  permissions?: Array<string>;
  hasChildren?: boolean;
}

export interface NatureDefinition {
  id: number;
  code: string;
  icon?: string;
  characteristics?: Array<CharacteristicRef>;
  parents: Array<Pick<NatureDefinition, 'id'>>;
}

export interface NavRoots {
  roots: Array<SystemObject>;
}

export interface SystemObjectRef {
  id: number;
}

export interface CharacteristicRef {
  code: string;
}

export interface IconReference {
  label: string;
  library: string;
  name: string;
}

export interface SystemObjectDetails extends SystemObject {
  description?: string;
  images?: Array<{ id: number; image: string }> | null;
  icon?: IconReference;
  labels?: Array<string>;
  dependents?: Array<Partial<SystemObject>>;
}

export interface SystemObjectData {
  object: SystemObject;
}

export interface GraphNode {
  id: number;
  loaded: boolean;
  loading: boolean;
  deleted: boolean;
  details: SystemObjectDetails;
  facts: Record<string, any>;
  childNodes: Array<number>;
}

export interface SystemObjectResult {
  object: {
    id: number;
    properties: Array<Property>;
  };
}

export interface CategorizedObjects {
  id: number;
  natureTitle: string;
  objects: Array<GraphNode>;
}

export enum PropertyAccess {
  READ = 'READ',
  WRITE = 'WRITE',
  PRIVATE = 'PRIVATE',
}

export interface PropertyInput {
  access?: PropertyAccess;
  iri?: string;
  label: string;
  value?: string | number | Record<string, any> | null;
}

export interface Property extends PropertyInput {
  id: number;
  object: SystemObject;
}

export interface NatureInstanceField {
  value: Array<NatureInstance>;
  source: NatureInstance;
  created: string;
}

export interface NatureInstance {
  id: number;
  object: SystemObject;
  nature: NatureDefinition;
  parents: Array<NatureInstance>;
  children: Array<NatureInstance>;
  dependents: Array<NatureInstanceField>;
  remove: string;
}

export interface Dependent {
  soid: number;
  /**
   * Only exists if the dependent is a nature instance.
   */
  nature?: NatureInstance;
  name: string;
  icon: IconReference;
}

/**
 * Response type for nature-instance move and SystemObjectField retarget operations.
 * Mirrors SOS `MoveResult` as selected by the BFF `co4*` operations.
 */
export interface MoveResult {
  success: boolean;
  error?: string | null;
  requiredNiids: Array<number>;
  additionalNiids: Array<number>;
  movedNiids?: Array<number> | null;
  blockers?: Array<{
    niid?: number;
    blocker: number;
    reason: string;
  }> | null;
}

export interface MoveResultResponse {
  result: MoveResult;
}

/**
 * Response type for node removal operations (nature instances and system objects)
 * @interface RemoveNodeResponse
 */
export interface RemoveNodeResponse {
  result: {
    /** Whether the removal operation was successful */
    success: boolean;
    /** Error message if the operation failed */
    error?: string;
    /** Array of objects that are blocking the removal operation */
    blockers: Array<{
      /** Nature instance ID of the blocker (if applicable) */
      niid?: number;
      /** System object ID of the blocker (if applicable) */
      soid?: number;
      /** ID of the blocking entity */
      blocker: number;
      /** Reason why the removal is blocked */
      reason: string;
    }>;
    /** Array of nature instance IDs that were successfully removed */
    removedNiids?: Array<number>;
    /** Array of system object IDs that were successfully removed */
    removedSoids?: Array<number>;
  };
}

export interface ReferenceNature {
  niid: number;
  code: string;
  targetNiids: Array<number>;
  targetSoids: Array<number>;
}

export interface ReferenceNode {
  soid: number;
  parentSoid: number | null;
  name: string | null;
  icon: IconReference | null;
  structural: boolean;
  isTarget: boolean;
  natures: Array<ReferenceNature>;
  targetSoids: Array<number>;
}

export interface ReferenceTreeNode extends ReferenceNode {
  children: Array<ReferenceTreeNode>;
}

export interface ReferenceResult {
  nodes: Array<ReferenceNode>;
  totalReferences: number;
  hiddenReferences: number;
}

/** Mirrors core's `WidgetAlignment` enum. */
export type WidgetAlignment = 'LEFT' | 'CENTER' | 'RIGHT' | 'FULL_WIDTH';

/** Mirrors core's `WidgetSize` enum. */
export type WidgetSize = 'SMALL' | 'MEDIUM' | 'LARGE';

/**
 * One widget on the home dashboard, identified by `widgetType` plus `objectId` -
 * the same type can appear once per object. There is no server-side id: the
 * layout is stored as one opaque value, so nothing addresses a widget remotely.
 */
export interface DashboardWidget {
  widgetType: string;
  /**
   * The object this widget shows, or null. Whether it is a niid or a soid follows
   * from `widgetType`; the tile declares which prop to deliver it under.
   */
  objectId?: number | null;
  /** The user's name for it. Null leaves the widget its own default. */
  title?: string | null;
  config?: Record<string, unknown> | null;
  alignment: WidgetAlignment;
  size: WidgetSize;
  sortOrder: number;
}

/** The home dashboard's layout, as stored in the user's `home_dashboard` setting. */
export interface LandingPageDashboard {
  widgets: Array<DashboardWidget>;
}

export type LandingPageDashboardStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface LandingPageDashboardState {
  status: LandingPageDashboardStatus;
  /** Set only when the load failed, in which case `dashboard` is null. */
  error?: string;
  dashboard: LandingPageDashboard | null;
  /** Widgets with a change in flight, so only that card shows as busy. */
  pending: ReadonlyArray<string>;
}
